import { useEffect, useState } from 'react';
import { IconCalendar, IconCalendarRepeat, IconCheck, IconDots, IconExternalLink, IconFileText, IconList, IconPlus, IconRefresh, IconSquareCheck, IconUsers } from '@tabler/icons-react';
import { useT } from '@hana/i18n';
import './i18n';
import { DataError, useList } from '@hana/data';
import { HrDock } from './HrDock';
import { HrStaff } from './HrStaff';
import { HrOrg } from './HrOrg';
import { HeaderTool, Page, PageHeader } from '@hana/shell';
import { Button, Chip, Segmented, Skeleton, StateView, useToast, type ScreenState } from '@hana/ui';
import type { HrEvent } from './types';
import {
  AGE_BANDS, DEPT_STATS, ORG_KPIS, STAFF_KPIS, GENDER, HEADCOUNT, HR_TABS, LEAVE_BY_MONTH, MOVES, OVERVIEW_KPIS, TOP_LEAVE, linePoints, type HrTab,
} from './overview';
import s from './hr.module.css';

// Mint-HR · tab Tổng quan (design-specs/Mint-HR.md). Chỉ đọc; nút ghi báo toast. Không lương / tiền.

function readState(): ScreenState | null {
  const v = new URLSearchParams(location.search).get('state');
  return v === 'loading' || v === 'empty' || v === 'error' || v === 'noperm' ? v : null;
}

const TONE: Record<HrEvent['tone'], string> = { warn: s.tWarn ?? '', pri: s.tPri ?? '', ok: s.tOk ?? '', mute: s.tMute ?? '' };
const actionIcon = (e: HrEvent) => (e.to === 'training' ? IconExternalLink : e.action.startsWith('Gia hạn') ? IconCalendarRepeat : e.to ? IconFileText : null);
const MONTHS = HEADCOUNT.map((_, i) => `T${i + 1}`);
const fmt1 = (n: number) => String(n).replace('.', ',');

export function HrOverview() {
  const toast = useToast();
  const t = useT();
  const { data, error, isLoading, isError, refetch } = useList('hrEvent', { limit: 100 });
  const forced = readState();
  const { data: staffData } = useList('hrStaff', { limit: 100 });
  const { data: orgData } = useList('hrOrgBlock', { limit: 100 });
  const [tab, setTab] = useState<HrTab>(() => (new URLSearchParams(location.search).get('tab') === 'staff' ? 'staff' : new URLSearchParams(location.search).get('tab') === 'org' ? 'org' : 'ov'));
  const [done, setDone] = useState<ReadonlySet<number>>(new Set());
  const events = data?.items ?? [];
  const [wide, setWide] = useState(() => matchMedia('(min-width: 1800px)').matches);
  const [dk, setDk] = useState<'appr' | 'task' | null | undefined>(undefined);
  useEffect(() => {
    const mq = matchMedia('(min-width: 1800px)');
    const on = () => setWide(mq.matches);
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, []);
  const dockKind = dk === undefined ? (wide ? 'appr' : null) : dk;

  let state: ScreenState = 'ready';
  if (forced) state = forced;
  else if (isLoading) state = 'loading';
  else if (isError) state = error instanceof DataError && error.code === 'noperm' ? 'noperm' : 'error';
  else if (events.length === 0) state = 'empty';

  const noWrite = () => toast({ msg: t('hr.noWrite') });
  const pickTab = (k: HrTab) => (k === 'ov' || k === 'staff' || k === 'org' ? setTab(k) : toast({ msg: t('hr.ov.tabClosed') }));
  const tabs = HR_TABS.map((x) => ({ key: x.key, label: t(`hr.tab.${x.key}`), n: 'n' in x ? x.n : undefined }));

  const pts = linePoints(HEADCOUNT);
  const line = pts.map((p, i) => `${i ? 'L' : 'M'}${p.x.toFixed(1)} ${p.y.toFixed(1)}`).join(' ');
  const base = 110;
  const area = `${line} L${pts[pts.length - 1]!.x.toFixed(1)} ${base} L16 ${base} Z`;
  const vin = MOVES.reduce((a, m) => a + m[0], 0);
  const vout = MOVES.reduce((a, m) => a + m[1], 0);
  const open = events.filter((_, i) => !done.has(i)).length;
  const maxLv = Math.max(...LEAVE_BY_MONTH);

  return (
    <Page kind="table" dock={dockKind ? <HrDock key={dockKind} initial={dockKind} onClose={() => setDk(null)} onWrite={(msg) => toast({ msg: t('hr.ov.writeSuffix', { msg }) })} /> : undefined}>
      <PageHeader
        title={t('hr.ov.title')}
        meta={
          <>
            <Chip label={t('hr.kr.period')} icon={<IconCalendar size={14} aria-hidden="true" />}>10/2026</Chip>
            <Chip label={t('hr.ov.updated')} icon={<IconRefresh size={14} aria-hidden="true" />}>08:42</Chip>
          </>
        }
        actions={
          <>
            {!wide && <HeaderTool label={t('hr.ov.apprWait')} icon={IconSquareCheck} n={5} active={dockKind === 'appr'} onClick={() => setDk('appr')} />}
            {!wide && <HeaderTool label={t('hr.ov.tasks')} icon={IconList} n={5} active={dockKind === 'task'} onClick={() => setDk('task')} />}
            <HeaderTool label={t('hr.ov.more')} icon={IconDots} />
            <Button variant="primary" icon={<IconPlus size={16} aria-hidden="true" />} onClick={noWrite}>
              {tab === 'org' ? t('hr.ov.assignSeat') : t('hr.ov.newLeave')}
            </Button>
          </>
        }
      />
      <div className={s.page}>
        <div className={s.tabs}>
          <Segmented label={t('hr.ov.group')} value={tab} onChange={pickTab} items={tabs} />
        </div>
        <StateView
          state={state}
          title={state === 'empty' ? t('hr.ov.emptyT') : undefined}
          desc={state === 'empty' ? t('hr.ov.emptyD') : undefined}
          icon={state === 'empty' ? <IconUsers size={26} stroke={1.8} /> : undefined}
          action={state === 'error' ? <Button onClick={() => void refetch()}>{t('hr.retry')}</Button> : undefined}
          skeleton={<Skeleton rows={9} />}
        >
          <div className={`${s.kpis} ${s.kpisOv}`}>
            {(tab === 'staff' ? STAFF_KPIS : tab === 'org' ? ORG_KPIS : OVERVIEW_KPIS).map((k) => (
              <div key={k.k} className={s.kpi}>
                <div className={s.kpiK}>{k.k}</div>
                <div className={s.kpiV}>
                  <b className={k.tone === 'warn' ? s.warn : undefined}>{k.v}</b>
                  <span>{k.u}</span>
                </div>
              </div>
            ))}
          </div>
          {tab === 'org' && <HrOrg blocks={orgData?.items ?? []} />}
          {tab === 'staff' && <HrStaff staff={staffData?.items ?? []} total={38} />}
          {tab === 'ov' && (<>
          <div className={s.grid}>
            <div className={s.col}>
              <section className={s.card} aria-label={t('hr.ov.upcoming')}>
                <div className={s.cardHd}>
                  <h2>{t('hr.ov.upcomingHd')}</h2>
                  <span>{t('hr.ov.toHandle', { n: open })}</span>
                </div>
                {events.map((e, i) => {
                  const isDone = done.has(i);
                  const Ico = actionIcon(e);
                  return (
                    <div key={`${e.kind}-${e.who}`} className={`${s.ev} ${isDone ? s.evDone : ''}`}>
                      <span className={`${s.evKind} ${TONE[e.tone]}`}>{e.kind}</span>
                      <div className={s.evTx}>
                        <b>{e.who}</b>
                        <div>{e.sub}</div>
                      </div>
                      <span className={`${s.evDue} ${i < 3 ? s.warn : ''}`}>{e.due}</span>
                      {!isDone && e.action && (
                        <button type="button" className={s.evAct} onClick={noWrite}>
                          {Ico && <Ico size={16} stroke={1.8} aria-hidden="true" />}
                          {e.action}
                        </button>
                      )}
                      {!isDone && (
                        <button type="button" className={s.evOk} aria-label={t('hr.ov.handled')} title={t('hr.ov.handled')} onClick={() => setDone(new Set(done).add(i))}>
                          <IconCheck size={16} stroke={1.8} aria-hidden="true" />
                        </button>
                      )}
                      {isDone && (
                        <span className={s.evClosed}>
                          <IconCheck size={14} stroke={2} aria-hidden="true" /> {t('hr.ov.handled')}
                        </span>
                      )}
                    </div>
                  );
                })}
              </section>
              <section className={s.card} aria-label={t('hr.ov.moves')}>
                <div className={s.cardHd}>
                  <h2>{t('hr.ov.movesHd')}</h2>
                  <span className={s.legend}>
                    <span><i className={s.lgLine} />{t('hr.ov.lgWorking')}</span>
                    <span><i className={s.lgIn} />{t('hr.ov.lgIn')}</span>
                    <span><i className={s.lgOut} />{t('hr.ov.lgOut')}</span>
                  </span>
                </div>
                <svg role="img" aria-label={t('hr.ov.chartAria', { list: HEADCOUNT.map((v, i) => `${MONTHS[i]} ${v}`).join(', '), vin, vout })} width="100%" height="132" viewBox="0 0 520 132" className={s.chart}>
                  <line x1="10" y1={base} x2="510" y2={base} className={s.axis} />
                  <path d={area} className={s.area} />
                  <path d={line} className={s.line} />
                  {pts.map((p, i) => {
                    const edge = i === 0 || i === pts.length - 1;
                    return (
                      <g key={MONTHS[i]}>
                        {i === pts.length - 1 && <circle cx={p.x} cy={p.y} r="4.5" className={s.dot} />}
                        <text x={p.x} y={p.y - 9} textAnchor="middle" className={s.pv} opacity={edge ? 1 : 0}>{HEADCOUNT[i]}</text>
                        <text x={p.x} y="127" textAnchor="middle" className={s.pm}>{MONTHS[i]}</text>
                      </g>
                    );
                  })}
                </svg>
                <svg aria-hidden="true" width="100%" height="30" viewBox="0 0 520 30" className={s.mov}>
                  {MOVES.map(([a, b], i) => (
                    <g key={MONTHS[i]}>
                      <rect x={(pts[i]!.x - 9).toFixed(1)} y={30 - a * 12} width="8" height={a * 12} rx="2" className={s.bIn}><title>{t('hr.ov.barTip', { m: i + 1, a, b })}</title></rect>
                      <rect x={(pts[i]!.x + 1).toFixed(1)} y={30 - b * 12} width="8" height={b * 12} rx="2" className={s.bOut}><title>{t('hr.ov.barTip', { m: i + 1, a, b })}</title></rect>
                    </g>
                  ))}
                </svg>
                <div className={s.note}>{t('hr.ov.note', { vin, vout })}</div>
              </section>
            </div>
            <div className={s.col}>
              <section className={s.card} aria-label={t('hr.ov.leave')}>
                <div className={s.cardHd}>
                  <h2>{t('hr.ov.leaveHd')}</h2>
                  <span>{t('hr.ov.leaveSub')}</span>
                </div>
                <div className={s.bars}>
                  {LEAVE_BY_MONTH.map((v, i) => (
                    <div key={MONTHS[i]} title={t('hr.ov.leaveTip', { m: i + 1, v })} className={s.bar}>
                      <span>{v}</span>
                      <div className={i === LEAVE_BY_MONTH.length - 1 ? s.barNow : undefined} style={{ height: Math.round((v / maxLv) * 80) }} />
                      <span className={s.barK}>{MONTHS[i]}</span>
                    </div>
                  ))}
                </div>
                <div className={s.sec}>{t('hr.ov.topLeave')}</div>
                {TOP_LEAVE.map(([who, v]) => (
                  <div key={who} className={s.top}>
                    <span>{who}</span>
                    <div className={s.track}><div style={{ width: `${Math.round((v / 12) * 100)}%` }} /></div>
                    <span className={s.topV}>{t('hr.ov.days', { v: fmt1(v) })}</span>
                  </div>
                ))}
              </section>
              <section className={s.card} aria-label={t('hr.ov.mix')}>
                <div className={s.cardHd}>
                  <h2>{t('hr.ov.mix')}</h2>
                  <span title={t('hr.ov.mixTip')}>{t('hr.ov.mixSub')}</span>
                </div>
                <div className={s.mix}>
                  <div>
                    <div className={s.mixK}>{t('hr.ov.gender')}</div>
                    {GENDER.map(([k, v], i) => (
                      <div key={k} className={`${s.mixRow} ${s.g}`}>
                        <span>{k}</span>
                        <div className={s.track}><div className={i ? s.violet : undefined} style={{ width: `${Math.round((v / 37) * 100)}%` }} /></div>
                        <b>{v}</b>
                      </div>
                    ))}
                  </div>
                  <div>
                    <div className={s.mixK}>{t('hr.ov.age')}</div>
                    {AGE_BANDS.map(([k, v]) => (
                      <div key={k} className={`${s.mixRow} ${s.a}`}>
                        <span>{k}</span>
                        <div className={s.track}><div style={{ width: `${Math.round((v / 19) * 100)}%` }} /></div>
                        <b>{v}</b>
                      </div>
                    ))}
                  </div>
                </div>
              </section>
            </div>
          </div>
          <section className={s.card} aria-label={t('hr.ov.byDept')}>
            <table className={s.table} aria-label={t('hr.ov.byDept')}>
              <colgroup>
                <col style={{ width: '22%' }} /><col style={{ width: '20%' }} /><col style={{ width: '10%' }} /><col style={{ width: '10%' }} /><col style={{ width: '10%' }} /><col style={{ width: '28%' }} />
              </colgroup>
              <thead>
                <tr>
                  <th scope="col">{t('hr.ov.thDept')}</th>
                  <th scope="col">{t('hr.ov.thFill')}</th>
                  <th scope="col" className={s.right}>{t('hr.ov.thGap')}</th>
                  <th scope="col" className={s.right}>{t('hr.ov.thMgr')}</th>
                  <th scope="col" className={s.right}>{t('hr.ov.thAge')}</th>
                  <th scope="col">{t('hr.ov.thNote')}</th>
                </tr>
              </thead>
              <tbody>
                {DEPT_STATS.map((d) => {
                  const gap = d.plan - d.act;
                  return (
                    <tr key={d.name}>
                      <td className={s.dn}>{d.name}</td>
                      <td>
                        <div className={s.fill}>
                          <div role="img" aria-label={t('hr.ov.fillAria', { name: d.name, act: d.act, plan: d.plan })} className={s.fillBar}>
                            <div className={s.track} style={{ width: `${Math.round((d.plan / 14) * 100)}%` }}>
                              <div className={d.act < d.plan ? s.amber : undefined} style={{ width: `${Math.round((d.act / Math.max(d.plan, 1)) * 100)}%` }} />
                            </div>
                          </div>
                          <span className={s.fillN}><b>{d.act}</b><span>/{d.plan}</span></span>
                        </div>
                      </td>
                      <td className={`${s.right} ${gap ? s.warnB : s.mute}`}>
                        {gap ? gap : '—'}
                      </td>
                      <td className={s.right}>{d.mgr}</td>
                      <td className={s.right}>{d.age}</td>
                      <td className={s.noteTd}>{d.note}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </section>
          </>)}
        </StateView>
      </div>
    </Page>
  );
}
